import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import { Row, Col } from 'react-bootstrap';
import useElementWidth from '../../../../modules/react/hooks/useElementWidth';
import useCurrentPage from '../../../hooks/useCurrentPage';
import { isUnderColumnCount } from '../../../../modules/theme/grid';
import FacilitiesLegend from '../../stateless/FacilitiesLegend';
import FacilitiesMap from '../FacilitiesMap';
import styles from './styles.scss';

const propTypes = {
  initialCenter: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  initialZoom: PropTypes.number,
  facilities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      location: PropTypes.shape({
        coordinates: PropTypes.arrayOf(
          PropTypes.number,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  ).isRequired,
  primaryFacilityId: PropTypes.string,
  title: PropTypes.string,
};

const defaultProps = {
  initialCenter: undefined,
  initialZoom: undefined,
  primaryFacilityId: undefined,
  title: undefined,
};

const FacilitiesMapWithLegend = ({
  initialCenter,
  initialZoom,
  facilities,
  primaryFacilityId,
  title,
  ...rest
}) => {
  const [parentRef, width] = useElementWidth();

  const { primaryColorClass } = useCurrentPage();

  const [hoveredFacility, setHoveredFacility] = useState();
  const [focusedFacility, setFocusedFacility] = useState();

  const [center, setCenter] = useState(initialCenter);
  const [zoom] = useState(initialZoom);

  // We stack the map and legend when their parent container is below 9 columns
  // in width because they start to look funky next to each other in such a small
  // space.
  const [mapLg, legendLg] = isUnderColumnCount(9, width) ? [12, 12] : [8, 4];

  const clearFocusedFacility = () => {
    setFocusedFacility();
  };

  const setAndCenterFocusedFacility = (facility) => {
    const [longitude, latitude] = facility.location.coordinates;

    setFocusedFacility(facility);
    setCenter({ latitude, longitude });
  };

  const titleClasses = cx(
    styles.title,
    primaryColorClass,
  );

  return (
    <div className={styles.container} ref={parentRef}>
      <Row>
        <Col xs={12} sm={12} md={12} lg={mapLg} className={styles.map}>
          <FacilitiesMap
            {...rest}
            activeFacility={hoveredFacility}
            center={center}
            facilities={facilities}
            focusedFacility={focusedFacility}
            onMapClick={clearFocusedFacility}
            onPopupDismiss={clearFocusedFacility}
            onMarkerClick={setAndCenterFocusedFacility}
            primaryFacilityId={primaryFacilityId}
            zoom={zoom}
          />
        </Col>
        <Col xs={12} sm={12} md={12} lg={legendLg} className={styles.legend}>
          {title && (
            <h2 className={titleClasses}>
              {title}
            </h2>
          )}
          <FacilitiesLegend
            facilities={facilities}
            onItemHover={setHoveredFacility}
            primaryFacilityId={primaryFacilityId}
            showPrimaryFacility={false}
          />
        </Col>
      </Row>
    </div>
  );
};

FacilitiesMapWithLegend.propTypes = propTypes;
FacilitiesMapWithLegend.defaultProps = defaultProps;

FacilitiesMapWithLegend.fragments = {
  facility: gql`
    fragment FacilitiesMapWithLegendFacility on Facility {
      id
      applicableFeesEnabled
      location {
        coordinates
      }
      ...FacilitiesLegendFacility
      ...FacilitiesMapFacility
    }

    ${FacilitiesLegend.fragments.facility}
    ${FacilitiesMap.fragments.facility}
  `,
};

export default FacilitiesMapWithLegend;
